'use client'

import * as React from 'react'
import { ChevronDown, MapPin, X } from 'lucide-react'
import { useSearchStore } from '@/lib/store'
import { useI18n } from '@/lib/i18n'
import { czechPlural, fmtCZK } from '@/components/roostr'
import { MapRowCard } from './map-row-card'
import type { Listing } from '@/lib/types'

// District-grouped side list for the desktop split map view — design
// search.jsx:387-396 (grouping + per-district medians) and 623-651 (sticky
// group headers + MapRowCard rows). Groups sort by size, biggest first.

// Height of the sticky jump-to-district bar; group headers stick right below
// it and scrollIntoView offsets by it (scroll-mt) so headers never hide.
const JUMP_BAR_H = 49
// The "In view" strip above the jump bar — same height budget so the group
// headers' sticky offsets stay correct.
const VIEW_BAR_H = 34

export interface MapDistrictListProps {
  hoveredListingId: string | null
  selectedListingId: string | null
  onHover: (id: string | null) => void
  /** Card click — focus the listing on the map (see MapRowCard.onSelect). */
  onSelect?: (listing: Listing) => void
  onDistrictHover: (district: string | null) => void
  registerRef: (id: string, el: HTMLDivElement | null) => void
  /**
   * Listings currently inside the map viewport, in the map's order. The panel
   * follows the map: pan or zoom and this list re-renders to match what is on
   * screen (it used to show whichever 100-listing page the map had loaded).
   */
  listings: Listing[]
  /** Everything in view, including beyond the hydrated cap — for the count. */
  totalInView: number
  loading?: boolean
  /**
   * Real per-district counts for the WHOLE search (from the map's point
   * cloud), used by the jump dropdown — not the ≤60 hydrated cards.
   */
  districtCounts?: Map<string, number>
  /** Currently focused district (panel is in district mode). */
  focusDistrict?: string | null
  /** Enter district mode — parent fits the map and lists all its listings. */
  onFocusDistrict?: (district: string) => void
  onLeaveDistrict?: () => void
  /** District mode: total in the district and how many are listed so far. */
  districtTotal?: number
  districtShown?: number
  onShowMoreDistrict?: () => void
}

export function MapDistrictList({
  hoveredListingId,
  selectedListingId,
  onHover,
  onSelect,
  onDistrictHover,
  registerRef,
  listings,
  totalInView,
  loading,
  districtCounts,
  focusDistrict,
  onFocusDistrict,
  onLeaveDistrict,
  districtTotal = 0,
  districtShown = 0,
  onShowMoreDistrict,
}: MapDistrictListProps) {
  const { t } = useI18n()
  const { hiddenListings } = useSearchStore()
  const results = listings
  const isLoading = !!loading
  const rootRef = React.useRef<HTMLDivElement>(null)
  const groupRefs = React.useRef(new Map<string, HTMLDivElement>())

  // Czech needs three plural forms (1 byt / 2–4 byty / 5+ bytů); English maps
  // few → many.
  const homeWord = (n: number) =>
    czechPlural(n, t('search.home', 'home'), t('search.homeFew', 'homes'), t('search.homes', 'homes'))

  const groups = React.useMemo(() => {
    const byDistrict = new Map<string, Listing[]>()
    for (const l of results) {
      if (hiddenListings.has(l.id)) continue
      const district = l.district || 'Praha'
      const arr = byDistrict.get(district)
      if (arr) arr.push(l)
      else byDistrict.set(district, [l])
    }
    return Array.from(byDistrict.entries())
      .map(([district, listings]) => {
        const s = listings.map((x) => x.price).sort((a, b) => a - b)
        const m = s.length / 2
        const median = s.length % 2 ? s[(s.length - 1) / 2] : (s[m - 1] + s[m]) / 2
        return { district, listings, median }
      })
      .sort((a, b) => b.listings.length - a.listings.length)
  }, [results, hiddenListings])

  // The panel follows the map, so DON'T yank the scroll on every pan — that
  // would fight the user. (It used to reset per page change, which happened
  // only on an explicit pager click.)

  // "Jump" is district FOCUS when the parent supports it: fit the map to the
  // district and list ALL its listings. Falls back to scrolling the in-view
  // group for callers that don't.
  const jumpToDistrict = (district: string) => {
    if (onFocusDistrict) onFocusDistrict(district)
    else groupRefs.current.get(district)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // Dropdown = every district in the SEARCH with its real count (point cloud),
  // alphabetical (Czech collation). Falls back to the in-view groups when the
  // parent gave no counts.
  const jumpOptions = React.useMemo(() => {
    if (districtCounts && districtCounts.size > 0) {
      return [...districtCounts.entries()]
        .map(([district, n]) => ({ district, n }))
        .sort((a, b) => a.district.localeCompare(b.district, 'cs'))
    }
    return [...groups]
      .map((g) => ({ district: g.district, n: g.listings.length }))
      .sort((a, b) => a.district.localeCompare(b.district, 'cs'))
  }, [districtCounts, groups])
  const hasJumpBar = jumpOptions.length > 1

  // The panel is scoped to the map VIEWPORT, not the whole search — the
  // header above still says "4 491 results", so without a label this list of
  // ~40 reads as a contradiction. Say exactly what it is, and when the
  // hydration cap trims it, say that too.
  const shown = results.length
  const capped = totalInView > shown
  const viewLabel = capped
    ? t('search.inViewCapped', 'In view: {total} · showing first {shown}')
        .replace('{total}', String(totalInView))
        .replace('{shown}', String(shown))
    : t('search.inView', 'In view: {n}').replace('{n}', String(totalInView))

  // (In district mode the first page may still be loading — never show the
  // viewport empty state over it.)
  if (!groups.length && !isLoading && !focusDistrict) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center text-muted-foreground">
        <p className="text-sm">{t('search.noResultsInView', 'No listings in this part of the map')}</p>
        <p className="text-xs">{t('search.noResultsInViewHint', 'Zoom out or move the map to see more.')}</p>
      </div>
    )
  }

  return (
    <div ref={rootRef} className="flex flex-col gap-2.5 p-3">
      {/* "In view" strip — always visible, explains the panel's scope. */}
      {focusDistrict ? (
        // District mode — the whole district, not the viewport.
        <div
          className="sticky top-0 z-20 -mx-3 -mt-3 flex items-center gap-1.5 border-b border-outline-variant bg-primary-container px-3 text-[11.5px] text-on-primary-container"
          style={{ height: VIEW_BAR_H }}
        >
          <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="font-semibold">
            {focusDistrict} · {districtTotal} {homeWord(districtTotal)}
          </span>
          <button
            type="button"
            onClick={onLeaveDistrict}
            className="ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold underline-offset-2 hover:underline"
          >
            <X className="h-3 w-3" aria-hidden />
            {t('search.districtBack', 'Back to map view')}
          </button>
        </div>
      ) : (
        <div
          className="sticky top-0 z-20 -mx-3 -mt-3 flex items-center gap-1.5 border-b border-outline-variant bg-background px-3 text-[11.5px] text-ink-secondary"
          style={{ height: VIEW_BAR_H }}
          title={t('search.inViewHint', 'The list follows the map — pan or zoom to change what is shown here.')}
        >
          <MapPin className="h-3.5 w-3.5 shrink-0 text-ink-muted" aria-hidden />
          <span className="font-semibold text-foreground">{viewLabel}</span>
          <span className="text-ink-muted">· {t('search.inViewFollows', 'follows the map')}</span>
        </div>
      )}

      {/* Jump-to-district dropdown — sticks under the "In view" strip */}
      {hasJumpBar && (
        <div
          className="sticky z-10 -mx-3 border-b border-outline-variant bg-background px-3 py-2"
          style={{ height: JUMP_BAR_H, top: VIEW_BAR_H }}
        >
          <div className="relative">
            <select
              value=""
              onChange={(e) => {
                if (e.target.value) jumpToDistrict(e.target.value)
              }}
              aria-label={t('search.jumpToDistrict', 'Jump to district…')}
              className="h-8 w-full cursor-pointer appearance-none rounded-full border border-outline bg-surface pl-3.5 pr-9 text-[12px] font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">{t('search.jumpToDistrict', 'Jump to district…')}</option>
              {jumpOptions.map((g) => (
                <option key={g.district} value={g.district}>
                  {g.district} · {g.n} {homeWord(g.n)}
                </option>
              ))}
            </select>
            <ChevronDown
              aria-hidden
              className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted"
            />
          </div>
        </div>
      )}

      {groups.map((g) => (
        <div
          key={g.district}
          ref={(el) => {
            if (el) groupRefs.current.set(g.district, el)
            else groupRefs.current.delete(g.district)
          }}
          style={{ scrollMarginTop: VIEW_BAR_H + (hasJumpBar ? JUMP_BAR_H : 0) }}
        >
          {/* Sticky district header — hover spots the district on the map */}
          <div
            onMouseEnter={() => onDistrictHover(g.district)}
            onMouseLeave={() => onDistrictHover(null)}
            title={t('search.districtHoverHint', 'Hover to spot this district on the map')}
            className="sticky z-[5] flex items-baseline gap-1.5 bg-background px-1 pb-[5px] pt-1.5"
            style={{ top: VIEW_BAR_H + (hasJumpBar ? JUMP_BAR_H : 0) }}
          >
            <span className="text-[11px] font-extrabold uppercase tracking-[1.2px]">{g.district}</span>
            <span className="text-[11px] text-ink-muted">
              · {g.listings.length} {homeWord(g.listings.length)}
            </span>
            <span
              className="ml-auto font-mono text-[11px] font-bold text-ink-secondary"
              title={`${t('search.medianRentIn', 'Median rent in')} ${g.district}: ${fmtCZK(g.median)}`}
            >
              {t('search.medianShort', 'med')} {Math.round(g.median / 1000)}k
            </span>
          </div>
          <div className="mt-[2px] flex flex-col gap-2">
            {g.listings.map((l) => (
              <div key={l.id} ref={(el) => registerRef(l.id, el)}>
                <MapRowCard
                  listing={l}
                  active={hoveredListingId === l.id || selectedListingId === l.id}
                  onHover={onHover}
                  onSelect={onSelect}
                />
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* District mode: page through the whole district. */}
      {focusDistrict && districtShown < districtTotal && (
        <button
          type="button"
          onClick={onShowMoreDistrict}
          disabled={isLoading}
          className="mx-auto mt-1 mb-2 inline-flex items-center gap-1.5 rounded-full border border-outline bg-surface px-4 py-2 text-[12.5px] font-semibold text-foreground transition-transform active:scale-[0.97] disabled:opacity-60"
        >
          {isLoading
            ? t('search.loading', 'Loading…')
            : t('search.districtShowMore', 'Show more ({shown} of {total})')
                .replace('{shown}', String(Math.min(districtShown, districtTotal)))
                .replace('{total}', String(districtTotal))}
        </button>
      )}
    </div>
  )
}
